import { ChangeEvent, RefObject, useCallback, useMemo } from 'react';

import { Box, SearchInput, SearchInputField } from '@frontend/components';

import { SearchOverlay } from './SearchOverlay';
import SearchResults from './SearchResults';
import { useSearchDataContext } from './context';

export type SearchBarProps = {
  isActive: boolean;
  setIsActive: (newIsActiveValue: boolean) => void;
  searchInputRef: RefObject<HTMLInputElement>;
};

export const SearchBar = ({
  isActive = false,
  setIsActive,
  searchInputRef,
}: SearchBarProps) => {
  const { searchTerm, setSearchTerm } = useSearchDataContext();

  const resultsAreDisplayed = searchTerm !== '' && isActive;
  const overlayIsDisplayed = isActive;

  const onChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setSearchTerm(event.target.value);
      setIsActive(true);
    },
    [setIsActive, setSearchTerm],
  );

  const onClear = useCallback(() => {
    setSearchTerm('');
    setIsActive(false);
  }, [setIsActive, setSearchTerm]);

  const onFocus = useCallback(() => setIsActive(true), [setIsActive]);

  const width = useMemo(
    () =>
      isActive
        ? {
            base: 'calc(100vw)',
            tablet: 'calc(100vw - (2 * 20px + 40px) - 20px)', // width of Parts icon is 40px, padding on either side is 20px
            desktop: 'calc(100vw - 2 * 168px - 2 * 20px)', // width of logo is 168px, padding on either side is 20px
          }
        : undefined,
    [isActive],
  );

  return (
    <Box mr={2} display="flex" position="relative">
      <SearchInput
        zIndex="dropdown"
        size="md"
        width={width}
        value={searchTerm}
        onChange={onChange}
        onClear={onClear}
        onFocus={onFocus}
      >
        <SearchInputField
          ref={searchInputRef}
          borderBottomColor={
            overlayIsDisplayed ? 'separator.divider' : 'separator.border'
          }
          borderBottomLeftRadius={overlayIsDisplayed ? '0px' : undefined}
          borderBottomRightRadius={overlayIsDisplayed ? '0px' : undefined}
          placeholder={
            overlayIsDisplayed
              ? 'Search by quote number, part number, PDF text, notes or key words'
              : 'Search anything'
          }
        />
      </SearchInput>
      {resultsAreDisplayed && <SearchResults />}
      {overlayIsDisplayed && (
        <SearchOverlay
          onClick={() => setIsActive(false)}
          data-testid="overlay"
        />
      )}
    </Box>
  );
};
